import {
  Grid,
  Radio,
  Text,
  Option,
  StoreLogo,
  Button,
  Skeleton,
  Message,
  ButtonKinds,
  Divider,
  CONTENT_ALIGNMENT_VARIANT,
  useThemePerClass,
  useIsDesignLanguage,
  DesignLanguage,
} from '@afterpaytouch/core'
import { getPayableOrderData } from '@afterpaytouch/portal-api/consumer/checkout'
import {
  ConfirmPayNowRequest,
  ErrorCode,
  OrderResponse,
  isOverduePaymentResponse,
  OverduePaymentResponse,
  OverduePaymentSchedule,
} from '@afterpaytouch/portal-api/types'
import React, { FunctionComponent, ReactNode, useEffect, useState } from 'react'
import { CloseCheckout, Money as MoneyComponent, PaymentMethod } from '..'
import {
  useAppRouter,
  useHandleInitiateMutation,
  useHandlePaymentMethodsSetup,
  useHandleRecoverableSetup,
  useIsRecoverableFlowReadyToConfirm,
  useConfirmPayNowMutation,
  useSelectedPaymentMethod,
  useAppDispatch,
  useApplePay,
  selectRecoverableDeclinesPaymentMethod,
  useRecoverableFlowTraceId,
  setCardScanCheckpoint,
  resetApplePayError,
  resetRecoverableFlowError,
  setRecoverableFlowStatus,
  useApplePayState,
  useRecoverableFlowSelectedOrder,
  setRecoverableFlowSelectedOrder,
  useIsRecoverableFlowPageReady,
  useSetErrorKeys,
  useSetErrorFields,
  resetCheckoutError,
  useIsAfterpayAppGiftCardFlow,
  useCheckoutToken,
} from '../../state'
import { useCheckoutSizes } from '../../utils/responsive'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import styles from './styles.module.css'
import { isPortalApiError } from '@afterpaytouch/portal-api'
import { ApplePayStatus, CheckPoint, isCardScanRequiredErrorCode, nonTerminalErrors, PaymentCaptureType, PaymentMethodType } from '../../model'
import { Route } from '../../config/router'
import { CardFlowType, RecoverableTerminalErrorStatus } from '@afterpay/types'
import { pascalCase } from '@afterpay/utils'
import { capture } from '@afterpaytouch/integrations/monitoring'
import { Trans, useTranslation } from '../../utils/convergence'
import { useConsolidatedQuery, useShouldShowNotification } from './hooks'
import { isAmountNonZero } from './helper'
import { getChannelTranslation } from '../../utils/translation-helpers'
import { CHECKOUT_FLOW_TYPE, postCheckoutFlowMessage } from '../../integrations/inappbrowser-messenger'

interface Props {
  filterBy: ErrorCode
}

export const PayableActiveOrders: FunctionComponent<Props> = ({ filterBy }) => {
  const { t } = useTranslation()
  const { data, isLoading, isFetching, isSuccess: isQuerySuccess, isError, isOverdue } = useConsolidatedQuery(filterBy)

  const { textSize, headingSize, extraSmallTextSize, smallTextSize } = useCheckoutSizes()
  const router = useAppRouter()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const applepay = useApplePayState()
  const selectedOrder = useRecoverableFlowSelectedOrder()
  const selectedOrderAmt = isOverdue ? (selectedOrder as OverduePaymentResponse)?.totalAmount : (selectedOrder as OrderResponse)?.orderSummary?.totalOwed
  const testNameSpace = 'payable-active-orders'
  // Used only for i18n - fallback to plural localization if undefined
  const orderCount = isOverduePaymentResponse(data) ? data?.overduePaymentSchedules.length : data?.length ?? 2
  const isReadyToConfirm = useIsRecoverableFlowReadyToConfirm()
  const isArcadeDesignLangauge = useIsDesignLanguage(DesignLanguage.Arcade)
  const [clicked, setClicked] = useState(false)

  const initiateResponse = useHandleInitiateMutation(isQuerySuccess)
  useHandlePaymentMethodsSetup(initiateResponse, selectedOrder?.enabledCardTypes)
  const setupNotification = useHandleRecoverableSetup()
  const [notification, setNotification] = useState(setupNotification)
  const dispatch = useAppDispatch()
  const applePayHelper = useApplePay()

  const isConfirmDisabled = !isReadyToConfirm || clicked || notification?.severe === true
  const selectedPaymentMethod = useSelectedPaymentMethod()
  const traceId = useRecoverableFlowTraceId()
  const [payNow, { isSuccess: isConfirmPaySuccess, isError: isConfirmPayError, error: confirmPayError }] = useConfirmPayNowMutation()
  const isPageReady = useIsRecoverableFlowPageReady()
  const [errorKey, setErrorKey] = useState<string>()
  const [errorMessage, setErrorMessage] = useState<string>('')
  const shouldShowNotification = useShouldShowNotification(notification, clicked)
  const isAfterpayAppGiftCardFlow = useIsAfterpayAppGiftCardFlow()
  const checkoutToken = useCheckoutToken()
  const labelPlacement = isArcadeDesignLangauge ? 'Top' : 'Middle'
  const ctaKind: ButtonKinds = isArcadeDesignLangauge ? 'Primary' : 'Secondary'
  const closeBtnKind: ButtonKinds = isArcadeDesignLangauge ? 'Secondary' : 'Link'
  const themedStyles = useThemePerClass(styles)
  useSetErrorKeys(confirmPayError, setErrorKey)
  useSetErrorFields(isConfirmPayError, setErrorMessage, setClicked, setNotification, errorKey, applepay)

  const renderOrderDetails = (order: OrderResponse | OverduePaymentSchedule): JSX.Element => {
    const { channel, owedAmount, name, id } = getPayableOrderData(order)

    if (isArcadeDesignLangauge) {
      return (
        <Grid.Item sm={9}>
          <MoneyComponent bold size={textSize} value={owedAmount} testNameSpace={`${testNameSpace}-owed-amount`} />
          {isOverdue && isAmountNonZero((order as OverduePaymentSchedule).lateFee) && (
            <Text color='Danger' size='M' testNameSpace='late-fee' renderAs='div'>
              <Trans
                i18nKey='recoverableTerminalError:overduePaymentEligible.lateFee'
                components={{
                  lateFee: <MoneyComponent color='Danger' size='M' value={(order as OverduePaymentSchedule).lateFee} />,
                }}
              />
            </Text>
          )}
          <Text color='Subtle'>{`${name} • ${pascalCase(channel)} #${id}`}</Text>
        </Grid.Item>
      )
    }

    return (
      <Grid.Item>
        <div className={styles.merchantName}>
          <Text bold>{name}</Text>
        </div>
        <Text color='Gray40'>{t('recoverableTerminalError:orderDetails', { channel: getChannelTranslation(channel, t), orderId: id })}</Text>
      </Grid.Item>
    )
  }

  const renderLabel = (order: OrderResponse | OverduePaymentSchedule): JSX.Element => {
    const { owedAmount, name, merchantLogoUrl } = getPayableOrderData(order)
    const gridProps = {
      center: !isArcadeDesignLangauge,
      ...(isArcadeDesignLangauge && { justifyContent: CONTENT_ALIGNMENT_VARIANT.NONE }),
    }
    return (
      <Grid {...gridProps}>
        <Grid.FixedItem width={60}>
          <StoreLogo url={merchantLogoUrl} alt={name} />
        </Grid.FixedItem>
        {renderOrderDetails(order)}
        <Grid.Item sm={4}>
          {!isArcadeDesignLangauge && (
            <div className={styles.textRight}>
              <MoneyComponent color='Danger' bold size={textSize} value={owedAmount} />
              {isOverdue && isAmountNonZero((order as OverduePaymentSchedule).lateFee) && (
                <Text color='Gray40' size='S' testNameSpace='late-fee' renderAs='div'>
                  <Trans
                    i18nKey='recoverableTerminalError:overduePaymentEligible.lateFee'
                    components={{
                      lateFee: <MoneyComponent color='Gray40' size='S' value={(order as OverduePaymentSchedule).lateFee} />,
                    }}
                  />
                </Text>
              )}
            </div>
          )}
        </Grid.Item>
      </Grid>
    )
  }

  const renderSkeletonLabel = (): JSX.Element => {
    return (
      <div>
        {t('recoverableTerminalError:ctaTitle', { count: orderCount })}
        <Skeleton></Skeleton>
      </div>
    )
  }

  const renderOrderItem = (order: OrderResponse | OverduePaymentSchedule): JSX.Element => {
    const listOfOrder = isOverduePaymentResponse(data) ? data?.overduePaymentSchedules : data
    const orderToSave = isOverduePaymentResponse(data) ? data : (order as OrderResponse)
    if (listOfOrder?.length === 1) {
      return <Option key={order.id} stretch text={() => renderLabel(order)} />
    }

    return (
      <Radio.Button
        labelPlacement={labelPlacement}
        key={order.id}
        labelWidth='Full'
        size={'Medium'}
        label={() => renderLabel(order)}
        onChange={() => dispatch(setRecoverableFlowSelectedOrder(orderToSave))}
        value={order.id.toString()}
        testNameSpace={testNameSpace}
        roundedBorder={isArcadeDesignLangauge}
      />
    )
  }

  // Dummy skeleton UI
  const renderSkeleton = (): JSX.Element => {
    return (
      <div>
        <Option disabled icon='Afterpay' stretch text={renderSkeletonLabel()} />
        <Option disabled icon='Afterpay' stretch text={renderSkeletonLabel()} />
      </div>
    )
  }

  const renderTotalLateFees = (): ReactNode => {
    if (isArcadeDesignLangauge || !isOverdue) {
      return null
    }

    return (
      <>
        <Grid center>
          <Text bold>{t('recoverableTerminalError:totalLateFees')}</Text>
          <div className={styles.textRight}>
            <MoneyComponent
              locale={router.locale}
              bold
              size={textSize}
              value={(selectedOrder as OverduePaymentResponse)?.totalLateFee}
              loading={isLoading || isFetching}
              testNameSpace={`${testNameSpace}-total-late-fees`}
            />
          </div>
        </Grid>
        <Divider kind='Hairline' />
      </>
    )
  }

  const renderTotalAmountPayable = (): ReactNode => {
    if (isArcadeDesignLangauge) {
      return null
    }

    return (
      <Grid center>
        <Text bold>{t('recoverableTerminalError:totalPayable')}:</Text>
        <div className={styles.textRight}>
          <MoneyComponent
            locale={router.locale}
            bold
            size={headingSize}
            value={selectedOrderAmt}
            loading={isLoading || isFetching}
            testNameSpace={`${testNameSpace}-total-amount`}
          />
        </div>
      </Grid>
    )
  }

  const handleCloseCheckout = (): void => {
    if (isAfterpayAppGiftCardFlow) {
      postCheckoutFlowMessage(CHECKOUT_FLOW_TYPE.ON_GIFT_CARD_CLOSE)
    }
  }

  const getCardFlowTypeFromErrorCode = (errorCode: ErrorCode): CardFlowType | undefined => {
    switch (errorCode) {
      case ErrorCode.AmountTooHigh:
        return CardFlowType.RECOVERY_AMOUNT_TOO_HIGH
      case ErrorCode.MaxActiveOrdersReached:
        return CardFlowType.RECOVERY_MAX_ACTIVE_ORDERS_REACHED
      case ErrorCode.OverduePaymentsCheckoutPaymentEligible:
        return CardFlowType.RECOVERY_OVERDUE_PAYMENTS_CHECKOUT_PAYMENT_ELIGIBLE
    }
  }

  const finalisePayment = async (): Promise<void> => {
    const payload: ConfirmPayNowRequest = {
      storeToken: false, // whether or not to save the card for future use (if it's a new card)
      traceId,
      paymentMethodType: selectedPaymentMethod?.type as PaymentMethodType,
      flow: getCardFlowTypeFromErrorCode(filterBy),
      token: checkoutToken,
      ...(selectedPaymentMethod?.type === PaymentMethodType.CASH_APP_PAY && { paymentMethodId: selectedPaymentMethod.cashAppPay.id }),
    }

    try {
      await payNow(payload).unwrap()
    } catch (e) {
      if (isPortalApiError(e)) {
        if (!nonTerminalErrors.includes(e.errorCode)) {
          router.push(Route.TERMINAL_ERROR)
        } else if (isCardScanRequiredErrorCode(e.errorCode)) {
          setCardScanCheckpoint(CheckPoint.CheckoutConfirm)
          router.push(Route.CARD_SCAN)
        }
      } else {
        capture.warning('Unexpected error during payment confirmation', { ...e })
      }
    }
  }

  const handleClick = async (): Promise<void> => {
    setClicked(true)
    logEvent(TrackingEvent.PRESSED_LIMIT_EXCEEDED_CONFIRM_BUTTON, { limitExceeded: filterBy })
    await dispatch(selectRecoverableDeclinesPaymentMethod(selectedPaymentMethod))

    if (selectedPaymentMethod?.type === PaymentMethodType.APPLE_PAY) {
      applePayHelper.initialise(t, logEvent, PaymentCaptureType.ONE_TIME)
      try {
        await applePayHelper.startSession()
      } catch (e) {
        return
      }
    }

    finalisePayment()
  }

  useEffect(() => {
    setNotification(setupNotification)
  }, [setupNotification])

  useEffect(() => {
    if (isConfirmPaySuccess) {
      dispatch(resetApplePayError())
      dispatch(resetRecoverableFlowError())
      logEvent(TrackingEvent.PAID_OUTSTANDING_PAYMENT)
      dispatch(setRecoverableFlowStatus(RecoverableTerminalErrorStatus.Finished))
      router.replace(Route.LOGGED_IN)
    }

    return () => {
      if (isConfirmPaySuccess) {
        dispatch(resetCheckoutError())
      }
    }
    // logEvent renders unnecessarily
  }, [isConfirmPaySuccess, dispatch])

  useEffect(() => {
    if (applepay.status === ApplePayStatus.Cancelled) {
      setClicked(false)
    }
  }, [applepay.status])

  useEffect(() => {
    if (isError || initiateResponse.isError) {
      router.push(Route.TERMINAL_ERROR)
    }
  }, [isError, initiateResponse.isError])

  if (!isPageReady) {
    return null
  }

  return (
    <div>
      <div className={isOverdue ? themedStyles.descriptionWrapper : styles.descriptionWrapper}>
        <Text bold>{t('recoverableTerminalError:ctaTitle', { count: orderCount, context: isOverdue ? 'overdue' : undefined })}</Text>
        {(isLoading || isFetching) && <Skeleton />}
      </div>
      {(isLoading || isFetching) && <Grid>{renderSkeleton()}</Grid>}
      {!isError && (
        <>
          <Grid>
            <Radio.Group
              id='activeOrders'
              name='active-orders'
              defaultValue={
                isOverdue
                  ? (selectedOrder as OverduePaymentResponse)?.overduePaymentSchedules?.[0]?.id.toString()
                  : (selectedOrder as OrderResponse)?.id.toString()
              }
              indent
              reversed
              kind='Option'
            >
              {isOverduePaymentResponse(data) ? data?.overduePaymentSchedules.map(renderOrderItem) : data?.map(renderOrderItem)}
            </Radio.Group>
          </Grid>
          {renderTotalLateFees()}
          {renderTotalAmountPayable()}
          <div className={isOverdue ? themedStyles.paymentMethodWrapper : undefined}>
            <PaymentMethod negativeMargins={false} isLoading={!isReadyToConfirm} />
          </div>
          {shouldShowNotification && (
            <div className={styles.text}>
              <Message
                iconName='Card'
                heading={notification?.heading}
                description={notification?.message}
                testNameSpace={`recoverable-notification-${errorKey ?? ''}`}
              />
            </div>
          )}
          {errorMessage !== undefined && errorMessage.length > 0 && (
            <div className={styles.text}>
              <Text color='Fire' testNameSpace='recoverable-generic-error'>
                {errorMessage}
              </Text>
            </div>
          )}
          <Grid>
            <div className={styles.disclaimerWrapper}>
              <Text size={isArcadeDesignLangauge ? smallTextSize : extraSmallTextSize} color={'Gray40'}>
                {t('recoverableTerminalError:legalDisclaimer')}
              </Text>
            </div>
          </Grid>
          <Grid>
            <Grid.Item>
              <Button
                kind={ctaKind}
                padding='Fluid'
                loading={isLoading || isFetching}
                disabled={isConfirmDisabled}
                aria-label={t('recoverableTerminalError:ctaAria', { amt: selectedOrderAmt?.amount })}
                onClick={handleClick}
                testNameSpace='payableActiveOrders'
              >
                <Text bold renderAs='div'>
                  <Trans
                    i18nKey={'recoverableTerminalError:cta'}
                    components={{
                      amt: <MoneyComponent testNameSpace={`${testNameSpace}-cta`} bold size='M' color='IGNORE' value={selectedOrderAmt} />,
                    }}
                  />
                </Text>
              </Button>
            </Grid.Item>
          </Grid>
          <Grid>
            <Grid.Item>
              <CloseCheckout padding='Fluid' aria-label={t('common:closeCheckoutAria')} size='Regular' kind={closeBtnKind} onClick={handleCloseCheckout}>
                {t('recoverableTerminalError:backToStore')}
              </CloseCheckout>
            </Grid.Item>
          </Grid>
        </>
      )}
    </div>
  )
}
